import React, { useEffect, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { DropdownItem, DropdownProps } from './Dropdown.types';
import {
  DropdownMenuItemStyled,
  DropdownMenuStyled,
  DropdownSelectStyled,
  DropdownStyled,
} from './Dropdown.styled';
import { Icons, IconsDictionary } from '@/components/icon/Icon.types';
import { useToolbarNavigation } from '@/components/toolbar/Toolbar.utils';
import useOutsideClick from './useOutsideClick';

const Dropdown = ({
  id,
  title = 'Select',
  options,
  onSelect,
  isOpen,
}: DropdownProps) => {
  const handleChange = (item: DropdownItem) => {
    if (onSelect) onSelect(item.id);
  };

  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: dropdownRef,
    handler: () => {
      if (onSelect) onSelect(false);
    },
  });

  useEffect(() => {
    if (!isOpen)
      (
        dropdownRef.current?.querySelector(
          '[data-dropdown="audiences"]'
        ) as HTMLElement
      )?.focus();
  }, [isOpen]);

  const Icon = Icons[IconsDictionary.Dropdown];

  const isDisabled = !options.length;

  useToolbarNavigation('[data-dropdownid="dropdown-item"]', !!isOpen);

  return (
    <DropdownStyled $isOpen={!!isOpen} ref={dropdownRef}>
      <DropdownSelectStyled
        data-id="panel-btn"
        data-dropdown="audiences"
        id={id}
        aria-label="Toggle audiences dropdown"
        aria-haspopup="true"
        aria-expanded={isOpen}
        type="button"
        className={isOpen ? 'expanded' : isDisabled ? 'disabled' : ''}
        disabled={isDisabled}
        onClick={() => {
          if (onSelect) onSelect(!isOpen);
        }}
      >
        <span className="focus-ring" />
        <span>{title}</span>
        <Icon />
      </DropdownSelectStyled>
      {!!isOpen && (
        <FocusLock>
          <DropdownMenuStyled id={'audiences-dropdown'}>
            <div aria-label="Dropdown menu" className="dropdown-menu">
              <ul role="menu" aria-labelledby={id} aria-orientation="vertical">
                {options?.map((item, idx) => (
                  <DropdownMenuItemStyled
                    key={item.id}
                    data-dropdownid="dropdown-item"
                    onClick={() => handleChange(item)}
                    onKeyDown={evt => {
                      if (
                        evt.key === 'Escape' ||
                        (evt.key === 'ArrowLeft' && idx === 0)
                      ) {
                        evt.stopPropagation();
                        if (onSelect) onSelect(false);
                      }

                      if (['Enter', ' '].includes(evt.key)) {
                        handleChange(item);
                      }
                    }}
                    tabIndex={0}
                  >
                    <div className="audience-name">{item.name}</div>
                    <div className="audience-description">
                      {item.description}
                    </div>
                  </DropdownMenuItemStyled>
                ))}
              </ul>
            </div>
          </DropdownMenuStyled>
        </FocusLock>
      )}
    </DropdownStyled>
  );
};

export default Dropdown;
